import { Container, type ContainerOptions } from "../core/Container.js";
import type { DragPayload, KeyEventArgs, MouseEventArgs } from "../core/events.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ItemSlot } from "./ItemSlot.js";
import { directionOfKey, stepInGrid } from "./slotNavigation.js";
import { ITEM_DRAG_KIND, type ItemDragData, type ItemStack, type RarityTable } from "./items.js";

export interface HotbarOptions extends ContainerOptions {
  /** How many slots. */
  slots?: number;
  cellSize?: number;
  gap?: number;
  /** Starting contents. */
  items?: readonly (ItemStack | null)[];
  /**
   * The key for each slot, shown in its corner and watched for. Defaults to
   * 1-9 then 0, which is what ten slots are usually bound to.
   */
  keys?: readonly string[];
  vertical?: boolean;
  rarities?: RarityTable;
  onActivate?: (item: ItemStack, index: number) => void;
}

const DEFAULT_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

/**
 * A row of keybound ability or item slots, with per-slot cooldowns and a
 * global one across the lot.
 *
 * The keys are watched at the bar, not at the slots: pressing 3 has to fire
 * the third slot wherever the focus happens to be, and a slot that only
 * responded when focused would be a hotbar you have to click first.
 *
 * The global cooldown is the short shared lockout after any ability — it
 * grays every slot at once without disturbing the individual timers running
 * underneath, so a long cooldown is still counting down while the global one
 * blinks past. An item marked `offGlobal` is the exception: it fires through
 * the global cooldown, and its slot is left out of the wash.
 */
export class Hotbar extends Container {
  #slots: ItemSlot[] = [];
  #globalCooldown = 0;
  #globalCooldownMax = 0;
  /** The slot the keyboard is on, once the bar has focus. */
  #cursor = 0;
  /**
   * Whether the cursor has been *used* yet, as opposed to merely focused.
   *
   * Activating a window focuses its first tab stop, which is this grid — so
   * without this a window opening would ring cell zero, and the ring reads as
   * a selection the user never made. The browsers' `:focus-visible` draws the
   * same distinction for the same reason. Set by the first key or press that
   * engages the cursor, and cleared when focus leaves.
   */
  #engaged = false;

  cellSize: number;
  gap: number;
  vertical: boolean;
  rarities: RarityTable | undefined;

  constructor(options: HotbarOptions = {}) {
    const count = Math.max(1, Math.floor(options.slots ?? 10));
    const cellSize = options.cellSize ?? 48;
    const gap = options.gap ?? 4;
    const vertical = options.vertical ?? false;
    const along = count * cellSize + (count - 1) * gap;
    super({
      // Reachable by Tab as one control, with the arrows walking the slots —
      // the same roving cursor the inventory uses, so a pad drives both.
      tabStop: true,
      width: vertical ? cellSize : along,
      height: vertical ? along : cellSize,
      ...options,
    });
    this.cellSize = cellSize;
    this.gap = gap;
    this.vertical = vertical;
    this.rarities = options.rarities;

    const keys = options.keys ?? DEFAULT_KEYS;
    for (let i = 0; i < count; i++) {
      const slot = new ItemSlot({
        item: options.items?.[i] ?? null,
        index: i,
        keybind: keys[i] ?? "",
        rarities: this.rarities,
        width: cellSize,
        height: cellSize,
        // A hotbar slot is a target you press, not a tab stop you walk to.
        tabStop: false,
      });
      slot.on("drop", (e) => {
        e.accepted = this.#take(e.payload, i);
      });
      slot.on("itemactivate", () => this.activate(i));
      slot.on("dragend", (e) => {
        const data = e.payload.data as ItemDragData | undefined;
        if (!e.accepted || !data?.takenFromSource) return;
        this.setItem(i, data.remainder ?? null);
      });
      this.#slots.push(slot);
      this.controls.add(slot);
    }
    this.#layout();
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("focus", () => this.#syncCursor());
    this.on("blur", () => {
      this.#engaged = false;
      this.#syncCursor();
    });

    if (options.onActivate) {
      this.on("itemactivate", (index) => {
        const item = this.#slots[index]?.item;
        if (item) options.onActivate!(item, index);
      });
    }
  }

  get slots(): readonly ItemSlot[] {
    return this.#slots;
  }

  get items(): readonly (ItemStack | null)[] {
    return this.#slots.map((s) => s.item);
  }

  itemAt(index: number): ItemStack | null {
    return this.#slots[index]?.item ?? null;
  }

  setItem(index: number, item: ItemStack | null): void {
    const slot = this.#slots[index];
    if (!slot) return;
    slot.item = item;
    this.emit("change");
  }

  // --- keyboard cursor -----------------------------------------------------

  /** The slot the keyboard is on. */
  get cursorIndex(): number {
    return this.#cursor;
  }
  set cursorIndex(index: number) {
    const next = Math.min(Math.max(Math.floor(index), 0), Math.max(0, this.#slots.length - 1));
    if (next === this.#cursor) return;
    this.#cursor = next;
    this.#syncCursor();
    this.emit("selectionchanged");
  }

  /** Ring the cursor slot: focused, and actually engaged. See InventoryGrid. */
  #syncCursor(): void {
    const show = this.focused && this.#engaged;
    this.#slots.forEach((slot, i) => {
      const current = show && i === this.#cursor;
      if (slot.current === current) return;
      slot.current = current;
      slot.invalidate();
    });
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    // The digit bindings come first: they work wherever focus is, and a bar
    // with focus should not stop answering to them.
    if (this.handleKey(e)) return;
    const direction = directionOfKey(e.key);
    if (direction) {
      // A vertical bar is a grid one column wide; a horizontal one is a grid
      // one row tall. Either way the arrows walk it.
      const columns = this.vertical ? 1 : this.#slots.length;
      if (!this.#engage()) {
        this.cursorIndex = stepInGrid(this.#cursor, this.#slots.length, columns, direction);
      }
      e.handled = true;
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      this.#engage();
      this.activate(this.#cursor);
      e.handled = true;
    }
  }

  /** See `InventoryGrid`: true when this woke the cursor rather than moved it. */
  #engage(): boolean {
    if (this.#engaged) return false;
    this.#engaged = true;
    this.#syncCursor();
    return true;
  }

  #onPointerDown(e: MouseEventArgs): void {
    this.focus();
    this.#engage();
    const stride = this.cellSize + this.gap;
    const along = this.vertical ? e.y : e.x;
    const index = Math.floor(along / stride);
    if (index >= 0 && index < this.#slots.length) this.cursorIndex = index;
  }

  /** The key each slot answers to. */
  get keys(): readonly string[] {
    return this.#slots.map((s) => s.keybind);
  }

  setKey(index: number, key: string): void {
    const slot = this.#slots[index];
    if (!slot) return;
    slot.keybind = key;
    slot.invalidate();
  }

  // --- cooldowns -----------------------------------------------------------

  get globalCooldown(): number {
    return this.#globalCooldown;
  }

  /** Lock every slot briefly, as any ability does. */
  triggerGlobalCooldown(seconds: number): void {
    this.#globalCooldownMax = Math.max(0, seconds);
    this.#globalCooldown = this.#globalCooldownMax;
    this.invalidate();
  }

  /**
   * Run every cooldown down by `deltaSeconds` — the game's own clock rather
   * than the desktop's, so a paused game has paused cooldowns.
   */
  tickCooldowns(deltaSeconds: number): void {
    if (this.#globalCooldown > 0) {
      this.#globalCooldown = Math.max(0, this.#globalCooldown - deltaSeconds);
    }
    for (const slot of this.#slots) slot.tickCooldown(deltaSeconds);
    this.invalidate();
  }

  /** Fire slot `index`, if there is anything there and it is off cooldown. */
  activate(index: number): boolean {
    const slot = this.#slots[index];
    if (!slot || !slot.item || slot.cooldown > 0) return false;
    if (this.#globalCooldown > 0 && !slot.item.offGlobal) return false;
    this.emit("itemactivate", index);
    return true;
  }

  /** Fire whichever slot is bound to `key`. */
  activateKey(key: string): boolean {
    const index = this.#slots.findIndex((s) => s.keybind === key);
    return index >= 0 && this.activate(index);
  }

  /**
   * Handle a key press aimed at the bar.
   *
   * Not wired to an event here on purpose: where hotbar keys come from is the
   * game's decision — the desktop's key stream, its own input map, or a
   * gamepad — and a bar that grabbed the digits globally would fight every
   * text field on the screen.
   */
  handleKey(e: KeyEventArgs): boolean {
    if (e.ctrlKey || e.altKey || e.metaKey) return false;
    if (!this.activateKey(e.key)) return false;
    e.handled = true;
    return true;
  }

  // --- drops ---------------------------------------------------------------

  #take(payload: DragPayload, index: number): boolean {
    const data = payload.data as ItemDragData | undefined;
    const slot = this.#slots[index];
    if (!data?.item || !slot) return false;

    if (payload.source && this.contains(payload.source)) {
      // Rearranging the bar itself: swap, both ends written here.
      const from = (payload.source as ItemSlot).index;
      const here = slot.item;
      slot.item = data.item;
      const origin = this.#slots[from];
      if (origin) origin.item = here;
      this.emit("change");
      return true;
    }

    // From an inventory. A hotbar holds a *reference* to what to use, so the
    // item is copied rather than moved: dragging a potion onto the bar should
    // not take it out of the bag.
    slot.item = data.item;
    this.emit("change");
    return true;
  }

  override canAcceptDrop(payload: DragPayload): boolean {
    return payload.kind === ITEM_DRAG_KIND;
  }

  #layout(): void {
    const stride = this.cellSize + this.gap;
    this.#slots.forEach((slot, i) => {
      const along = i * stride;
      slot.setBounds(
        this.vertical ? 0 : along,
        this.vertical ? along : 0,
        this.cellSize,
        this.cellSize,
      );
    });
  }

  protected override onPaint(): void {
    // The bar itself is only its slots; the one thing it draws of its own is
    // the global-cooldown wash, and that has to go over them.
  }

  /**
   * The global-cooldown wash, drawn after the slots rather than behind them:
   * the point of it is that nothing under it is usable right now. One wash per
   * run of slots it holds, so an `offGlobal` slot stands clear of it, and a bar
   * with none of those is washed whole as before.
   */
  protected override paintChildren(rc: RenderContext, theme: Theme): void {
    super.paintChildren(rc, theme);
    if (this.#globalCooldownMax <= 0 || this.#globalCooldown <= 0) return;
    const fraction = this.#globalCooldown / this.#globalCooldownMax;
    const stride = this.cellSize + this.gap;
    const held = this.#slots.map((slot) => !slot.item?.offGlobal);
    for (const [first, last] of globalRuns(held)) {
      if (first === 0 && last === held.length - 1) {
        theme.drawGlobalCooldown(rc, { x: 0, y: 0, width: this.width, height: this.height }, fraction);
        continue;
      }
      const start = first * stride;
      const along = (last - first + 1) * stride - this.gap;
      theme.drawGlobalCooldown(
        rc,
        this.vertical
          ? { x: 0, y: start, width: this.width, height: along }
          : { x: start, y: 0, width: along, height: this.height },
        fraction,
      );
    }
  }
}

/**
 * The first and last index of each unbroken run of `true` — the slots one
 * global-cooldown wash covers.
 */
export function globalRuns(held: readonly boolean[]): [number, number][] {
  const runs: [number, number][] = [];
  let first = -1;
  held.forEach((h, i) => {
    if (h && first < 0) first = i;
    if (!h && first >= 0) {
      runs.push([first, i - 1]);
      first = -1;
    }
  });
  if (first >= 0) runs.push([first, held.length - 1]);
  return runs;
}
